import styles from "./RadioQuestion.module.scss";

import { KeyboardEvent, useCallback, useMemo } from "react";

import { ScaleSelector } from "@/modules/session-survey/components/ScaleSelector";
import { SurveyQuestionComponentProps } from "@/modules/session-survey/types/SurveyQuestionComponentProps";
import { AppRadioGroup } from "@/modules/shared/components/AppRadioGroup";

const FORWARD_KEYS = ["ArrowRight", "ArrowDown"];
const BACKWARD_KEYS = ["ArrowLeft", "ArrowUp"];
const MAX_HORIZONTAL_OPTION_COUNT = 10;
const MAX_HORIZONTAL_LABEL_LENGTH = 16;
const MAX_HORIZONTAL_LABEL_CHARACTERS = 40;

export const RadioQuestion = ({
  question,
  value,
  onAnswer,
}: SurveyQuestionComponentProps) => {
  const promptId = `${question.id}-prompt`;
  const bodyId = `${question.id}-body`;
  const metadata = question.clientMetadata;
  const selectedValue = typeof value === "string" ? value : undefined;
  const options = useMemo(
    () =>
      (question.options ?? []).map((option, index) => ({
        ...option,
        id: `${question.id}-option-${index}`,
      })),
    [question.id, question.options],
  );
  const horizontalLabels = options.map((option) => option.label);
  const hasCompactOptions =
    options.length > 0 &&
    options.length <= MAX_HORIZONTAL_OPTION_COUNT &&
    horizontalLabels.every(
      (label) => label.trim().length <= MAX_HORIZONTAL_LABEL_LENGTH,
    ) &&
    horizontalLabels.reduce((total, label) => total + label.length, 0) <=
      MAX_HORIZONTAL_LABEL_CHARACTERS;
  const isScale = question.type === "select" && hasCompactOptions;

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (
      !(event.target instanceof HTMLInputElement) ||
      event.target.type !== "radio"
    ) {
      return;
    }

    const radios = Array.from(
      event.currentTarget.querySelectorAll<HTMLInputElement>(
        'input[type="radio"]',
      ),
    );
    const currentIndex = radios.indexOf(event.target);
    let nextIndex: number | undefined;

    if (FORWARD_KEYS.includes(event.key)) {
      nextIndex = (currentIndex + 1) % radios.length;
    } else if (BACKWARD_KEYS.includes(event.key)) {
      nextIndex = (currentIndex - 1 + radios.length) % radios.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = radios.length - 1;
    }

    if (nextIndex === undefined) return;

    event.preventDefault();
    // Move focus only. Selecting auto-advances the survey, so committing must
    // remain an explicit Space/click action rather than a side effect of
    // exploring the options with arrow keys.
    radios[nextIndex].focus();
  }, []);

  return (
    <section className={styles.container} aria-labelledby={promptId}>
      <div className={styles.copy}>
        <h2
          id={promptId}
          className={`${styles.prompt} ${
            metadata?.shouldCenterTitle ? styles.centeredPrompt : ""
          }`}
        >
          {question.prompt}
        </h2>
        {metadata?.body ? (
          <p id={bodyId} className={styles.body}>
            {metadata.body}
          </p>
        ) : null}
      </div>

      {isScale ? (
        <ScaleSelector
          options={question.options ?? []}
          value={selectedValue}
          onSelect={onAnswer}
          ariaLabelledBy={promptId}
          ariaDescribedBy={metadata?.body ? bodyId : undefined}
          minLabel={metadata?.minLabel}
          maxLabel={metadata?.maxLabel}
        />
      ) : (
        <div
          className={styles.radioGroup}
          role="radiogroup"
          aria-labelledby={promptId}
          aria-describedby={metadata?.body ? bodyId : undefined}
          onKeyDown={handleKeyDown}
        >
          <AppRadioGroup
            name={question.id}
            options={options}
            selectedValue={selectedValue ?? ""}
            onChange={onAnswer}
          />
        </div>
      )}
    </section>
  );
};
